import isArray from 'lodash/isArray'
import React, { useMemo, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'
import {
  DASH,
  FETCH_USER_EVACUATION_STATUS_AFTER_MINUTE,
  FETCH_USER_SAFETY_LIST_AFTER_MINUTES,
} from '../../appConstants'
import iconCloseLogo from '../../assets/images/icons/icn-close-table.svg'
import iconMaximizeLogo from '../../assets/images/icons/icn-maximize.svg'
import iconMinimizeLogo from '../../assets/images/icons/icn-minimize.svg'
import noDataAvailable from '../../assets/images/no-data-available.svg'
import iconCircle from '../../assets/images/icons/Circle.svg'
import iconArrowUp from '../../assets/images/icons/arrow-up.svg'
import iconArrowDown from '../../assets/images/icons/arrow-down.svg'
import edit from '../../assets/images/icons/edit.svg'
import { useAuth } from '../../context/login/components/organisms/Protected/UserLogin'
import {
  FilterState,
  UserSafetyListRes,
  UserSafetyStatus,
  UserTablePos,
} from '../../data/types/DisasterRiskManagement'
import { DisasterService } from '../../services/disasterRiskManagement/DisasterRiskManagementService'
import {
  filterUserSafetyList,
  getDefaultEvacuationStatus,
  getDefaultUserStatus,
} from '../../utils/disasterRiskManagement/filterSafetyList'
import { getClassesForUserStatus } from '../../utils/commonUtils'
import { useIsCorrectMapCoordinatesResponse } from '../../hooks/queries/useMapCoordinates'
import { DEFAULT_POSITION, getBounds } from '.'
import { SetState } from '../../data/types/common'
import ReactDOM from 'react-dom'
import { useLatestDisasterStatus } from '../../hooks/queries/useLatestDisasterStatus'
import { useEvacuationHistory } from '../../hooks/queries/useEvacuationHistory'
import { EditStatusModal } from './EditStatusModal'
import { ProxyStatusModal } from './proxyStatusModal'

type UserSafetyListTableProps = {
  shouldSafetyListTableDisplay: boolean
  setShouldSafetyListTableDisplay: React.Dispatch<React.SetStateAction<boolean>>
  isTableMinimize: boolean
  setIsTableMinimize: React.Dispatch<React.SetStateAction<boolean>>
  filterState: FilterState
  tableRef: React.RefObject<HTMLDivElement>
  setPosition: SetState<UserTablePos>
  bounds: ReturnType<typeof getBounds>
  isStatusModelOpen: boolean
  isProxyModelOpen: boolean
  setIsStatusModelOpen: React.Dispatch<React.SetStateAction<boolean>> // Add setter for status modal
  setIsProxyModelOpen: React.Dispatch<React.SetStateAction<boolean>> // Add setter for proxy modal
}

const UserSafetyListTable: React.FC<UserSafetyListTableProps> = ({
  shouldSafetyListTableDisplay,
  setShouldSafetyListTableDisplay,
  isTableMinimize,
  setIsTableMinimize,
  filterState,
  tableRef,
  setPosition,
  bounds,
  isStatusModelOpen,
  isProxyModelOpen,
  setIsStatusModelOpen,
  setIsProxyModelOpen,
}) => {
  const { t } = useTranslation()
  const { currentTenantId } = useAuth()
  const [sortState, setSortState] = useState<{
    key: 'name' | null
    direction: 'asc' | 'desc' | null
  }>({ key: null, direction: null })

  const { data: latestDisasterStatus } = useLatestDisasterStatus()

  const disasterId = latestDisasterStatus?.data.status
    ? latestDisasterStatus?.data.disaster_id
    : undefined

  const {
    data: evacuationApiData,
    error: evacuationApiError,
  } = useEvacuationHistory()

  const { data: safetyListApiResponse } = useQuery({
    queryKey: ['user-safety-list', disasterId],
    enabled: !!disasterId,
    refetchInterval: FETCH_USER_SAFETY_LIST_AFTER_MINUTES * 60 * 1000,
    queryFn: () =>
      DisasterService.fetchUserSafetyList(Number(disasterId), currentTenantId),
  })

  const userSafetyListData = safetyListApiResponse?.data
  const evacuationHistoryData = evacuationApiData?.data?.evacuation_history
  const evacuationHistoryError = evacuationApiError
  const handleSortClick = (key: 'name') => {
    setSortState((prev) => {
      if (prev.key !== key) {
        return { key, direction: 'asc' }
      }
      if (prev.direction === 'asc') {
        return { key, direction: 'desc' }
      }
      if (prev.direction === 'desc') {
        return { key: null, direction: null }
      }
      return { key, direction: 'asc' }
    })
  }
  const [selectedUser, setSelectedUser] = useState<{
    id: string
    status: string
    proxyStatus: string | null
  } | null>(null)
  const tableData: UserSafetyListRes[] = useMemo(() => {
    const evacuationHistory = isArray(evacuationHistoryData)
      ? evacuationHistoryData
      : []

    const displayEvacuationFlag = !!evacuationApiData?.data
      ?.display_evacuation_status

    let data =
      filterUserSafetyList({
        evacuationHistoryRes: evacuationApiData?.data,
        filterState,
        safetyListData: safetyListApiResponse?.data,
      })?.filteredByPartnersDepartmentsEvacuationStatusAndSafetyStatus?.map(
        (userSafetyData) => {
          const matchingEvacuationStatus = evacuationHistory?.find(
            (evacData) =>
              evacData.user_id?.toLowerCase() ===
              userSafetyData?.user_id?.toLowerCase()
          )
          if (matchingEvacuationStatus) {
            return {
              ...userSafetyData,
              user_status: getDefaultUserStatus(
                userSafetyData.user_status as UserSafetyStatus
              ),
              evacuation_status: matchingEvacuationStatus.evacuation_status,
            }
          }

          const defaultEvacuationStatus = getDefaultEvacuationStatus({
            evacuationFlag: displayEvacuationFlag,
            evacuationHistoryData: evacuationHistory,
          })

          return {
            ...userSafetyData,
            user_status: getDefaultUserStatus(
              userSafetyData.user_status as UserSafetyStatus
            ),
            evacuation_status: defaultEvacuationStatus,
          }
        }
      ) ?? []

    if (sortState.key && sortState.direction) {
      data = [...data].sort((a, b) => {
        const aValue = a.user_name?.toLowerCase?.() ?? ''
        const bValue = b.user_name?.toLowerCase?.() ?? ''
        if (aValue < bValue) return sortState.direction === 'asc' ? -1 : 1
        if (aValue > bValue) return sortState.direction === 'asc' ? 1 : -1
        return 0
      })
    }

    return data
  }, [
    evacuationApiData?.data,
    evacuationHistoryData,
    filterState,
    safetyListApiResponse?.data,
    sortState,
  ])

  const tableAreaHeight = useTableAreaSize()

  return (
    <>
      <div
        ref={tableRef}
        className={
          'table-area' +
          (isTableMinimize ? ' table-minimize cursor-move' : '') +
          (shouldSafetyListTableDisplay ? ' table-show' : '')
        }
        style={{ height: `${tableAreaHeight}px` }}
      >
        <div className="uk-flex uk-flex-bottom uk-flex-right uk-width-1-1 column-gap-15 uk-padding-small user-table-header">
          <a
            id="minimize-btn"
            className={`icn-minimize ${
              isTableMinimize ? 'opacity-half pointer-events-none' : ''
            }`}
            onClick={() => {
              ReactDOM.flushSync(function () {
                setIsTableMinimize(true)
              })
            }}
          >
            <img
              // src="../../assets/images/icn-minimize.svg"
              src={iconMinimizeLogo}
              alt="icn-minimize"
            />
          </a>
          <a
            id="maximize-btn"
            className={`icn-maximize ${
              !isTableMinimize ? 'opacity-half pointer-events-none' : ''
            }`}
            onClick={() => {
              ReactDOM.flushSync(function () {
                setIsTableMinimize(false)
              })
            }}
          >
            <img
              // src="../../assets/images/icn-maximize.svg"
              src={iconMaximizeLogo}
              alt="icn-maximize"
            />
          </a>
          <a
            className="icn-close-table"
            onClick={() => setShouldSafetyListTableDisplay(false)}
          >
            <img
              // src="../../assets/images/icn-close-table.svg"
              src={iconCloseLogo}
              alt="icn-close"
            />
          </a>
        </div>
        <div className="uk-flex uk-overflow-auto table-data uk-text-center">
          <table className="uk-table uk-table-bordered uk-table-hover uk-table-divider">
            <thead className="uk-thead-sticky">
              <tr>
                <th
                  className="bg-grey-50 uk-text-center uk-position-relative uk-pointer cursor-pointer"
                  onClick={() => handleSortClick('name')}
                >
                  <Trans>TITLES.NAME</Trans>

                  <span className="uk-position-center-right mr-15 responsive-icons">
                    <img
                      src={iconArrowUp}
                      alt="icn-arrow-up"
                      style={{
                        filter:
                          sortState.key === 'name' &&
                          sortState.direction === 'asc'
                            ? 'invert(32%) sepia(95%) saturate(7471%) hue-rotate(201deg) brightness(95%) contrast(90%)'
                            : 'invert(29%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(90%) contrast(90%)',
                      }}
                    />
                    <img
                      src={iconArrowDown}
                      alt="icn-arrow-down"
                      style={{
                        marginLeft: '-5px',
                        filter:
                          sortState.key === 'name' &&
                          sortState.direction === 'desc'
                            ? 'invert(32%) sepia(95%) saturate(7471%) hue-rotate(201deg) brightness(95%) contrast(90%)'
                            : 'invert(29%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(90%) contrast(90%)',
                      }}
                    />
                  </span>
                </th>

                <th
                  className="bg-grey-50 uk-text-center"
                  style={{ width: '6em' }}
                >
                  <Trans>TITLES.GUEST_USER</Trans>
                </th>
                <th className="bg-grey-50 uk-text-center">
                  <Trans>TITLES.COMPANY</Trans>
                </th>
                <th className="bg-grey-50 uk-text-center">
                  <Trans>TITLES.AFFILIATION</Trans>
                </th>
                <th className="bg-grey-50 uk-text-center w-150">
                  <Trans>TITLES.TABLE_STATUS</Trans>
                </th>
                <th className="bg-grey-50 uk-text-center w-150">
                  <Trans>TITLES.EVACUATION_STATUS</Trans>
                </th>
                <th className="bg-grey-50 uk-text-center w-300">
                  <Trans>TITLES.PROXY_REPORT</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.length > 0 ? (
                tableData.map((data, index) => {
                  return (
                    <tr key={`${index}-${data.user_id}`}>
                      <td>{data.user_name || DASH}</td>
                      <td>
                        {data.user_type === 'GUEST' ? (
                          <img src={iconCircle} alt="icn-close" />
                        ) : (
                          DASH
                        )}
                      </td>
                      <td>{data?.company || DASH}</td>
                      <td>{data.department || DASH}</td>
                      <td
                        className={getClassesForUserStatus(data.user_status)}
                        style={{ position: 'relative' }}
                      >
                        {t(`LABELS.${data.user_status}`)}
                        <img
                          src={edit}
                          alt="edit"
                          className="edit-icon mb-5"
                          onClick={() => {
                            setSelectedUser({
                              id: data.user_id,
                              status: data.user_status,
                              proxyStatus: data.proxy_status,
                            })
                            setIsStatusModelOpen(true)
                          }}
                        />
                      </td>
                      <td>{t(`LABELS.${data.evacuation_status}`)}</td>

                      <td style={{ position: 'relative' }}>
                        <p className="mr-30 uk-margin-remove-bottom">
                          {data.proxy_status?.trim() ? data.proxy_status : DASH}
                        </p>
                        <img
                          src={edit}
                          alt="edit"
                          className="edit-icon mb-5"
                          onClick={() => {
                            setSelectedUser({
                              id: data.user_id,
                              status: data.user_status,
                              proxyStatus: data.proxy_status,
                            })
                            setIsProxyModelOpen(true)
                          }}
                        />
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  {/* --------- No Data Available --------- */}
                  <td colSpan={5}>
                    <div className="uk-flex uk-flex-middle uk-flex-center min-h-335">
                      <div className="uk-empty-section uk-text-center">
                        <img
                          src={noDataAvailable}
                          height={150}
                          width={150}
                          alt=""
                        />
                        <h4 className="font-20 color-grey-blue-95 mt-30">
                          <Trans>TITLES.DATA_NOT_FOUND</Trans>
                        </h4>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            {/* --------- No Data Available --------- */}
          </table>
        </div>
      </div>
      {isStatusModelOpen && selectedUser && (
        <EditStatusModal
          setIsStatusModelOpen={setIsStatusModelOpen}
          currentTenantId={currentTenantId}
          disasterId={Number(disasterId)}
          userId={selectedUser.id}
          currentStatus={selectedUser.status}
        />
      )}
      {isProxyModelOpen && selectedUser && (
        <ProxyStatusModal
          setIsProxyModelOpen={setIsProxyModelOpen}
          currentTenantId={currentTenantId}
          disasterId={Number(disasterId)}
          userId={selectedUser.id}
          proxyStatus={selectedUser.proxyStatus}
        />
      )}
    </>
  )
}

export default UserSafetyListTable

function useWindowSize() {
  const [height, setHeight] = useState<number | null>(null)

  React.useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return height
}

export function useTableAreaSize() {
  const windowHeight = useWindowSize()

  const navbar = document.querySelector('.uk-navbar')
  const footer = document.querySelector('.footer')

  if (windowHeight && navbar && footer) {
    return (
      footer.getBoundingClientRect().top - navbar.getBoundingClientRect().bottom
    )
  }

  return undefined
}
